import Vue from 'vue';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import * as oauth from 'oauth4webapi';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created,
 * it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        ...options,
        // Renew the access token with a rotating refresh token instead of an /authorize round
        // trip, so the token lifetime set on the API does not decide how often the user is
        // bounced to the login page. Falls back to silent auth if the refresh grant fails.
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        // Refresh tokens have to outlive the tab to be any use on reload or in a new tab.
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: redirectUri,
          audience: options.audience,
          // offline_access is what makes Auth0 issue the refresh token; the rest are the
          // SDK defaults, which are dropped as soon as scope is set explicitly.
          scope: 'openid profile email offline_access',
        },
      });

      try {
        // If the user is returning to the app after authentication..
        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          this.error = null;

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.loading = false;
      }
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(opts, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(opts, config);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          this.error = e;
          // eslint-disable-next-line
          console.warn(e);
        } finally {
          this.popupOpen = false;
        }

        // this.user = await this.auth0Client.getUser();
        // this.isAuthenticated = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = !!this.user;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently(o) {
        const token = await this.auth0Client.getTokenSilently(o);
        this.user = await this.auth0Client.getUser();
        return token;
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      },
    },
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  // eslint-disable-next-line no-shadow
  install(Vue, options) {
    // eslint-disable-next-line no-param-reassign
    Vue.prototype.$auth = useAuth0(options);
  },
};

const OAUTH_INTEGRATION_CLIENT_IDS = JSON.parse(
  process.env.VUE_APP_OAUTH_INTEGRATION_CLIENT_IDS ?? '{}',
);

// Authorize a third-party integration using PKCE
export async function redirectToAuthorizeWithPkce(provider, authorizeUrl, redirectUri, stateObj) {
  const clientId = OAUTH_INTEGRATION_CLIENT_IDS[provider];
  const verifier = oauth.generateRandomCodeVerifier();
  const challenge = await oauth.calculatePKCECodeChallenge(verifier);

  sessionStorage.setItem('pkce_verifier', verifier);

  const state = btoa(
    JSON.stringify({
      ...stateObj,
      csrfToken: oauth.generateRandomState(),
    }),
  );
  sessionStorage.setItem('oauth_state', state);

  const url = new URL(authorizeUrl);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);

  window.location.href = url.toString();
}

// Call to our backend to finish setting up third-party integration
export async function authenticateThirdPartyIntegration(provider, code, domain, state) {
  const localState = sessionStorage.getItem('oauth_state');
  if (state !== localState) {
    throw new Error('state mismatch, aborting auth flow');
  }
  const codeVerifier = sessionStorage.getItem('pkce_verifier');
  const redirectUri = window.location.origin + window.location.pathname;

  const requestUrl = new URL(
    `api/3/integrations/${provider}/authenticate`,
    process.env.VUE_APP_URL,
  );
  requestUrl.searchParams.append('code', code);
  requestUrl.searchParams.append('code_verifier', codeVerifier);
  requestUrl.searchParams.append('domain', domain);
  requestUrl.searchParams.append('redirect_uri', redirectUri);
  requestUrl.searchParams.append('state', state);

  const token = await getInstance().$auth.getTokenSilently();
  const response = await fetch(requestUrl, {
    method: 'post',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const { detail } = await response.json();
    throw new Error(detail ?? response.statusText);
  }
}
